import React, { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import "../styles/chat.css";

const SOCKET_SERVER_URL = "http://localhost:3005";

function Chat() {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [user, setUser] = useState(null);
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const [selectedUser, setSelectedUser] = useState(null);
  // Fetch user and token from local storage
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("access_token");

    if (storedUser && storedToken) {
      try {
        const parsedUser = JSON.parse(storedUser);
        console.log("Parsed user data:", parsedUser);

        setUser({
          ...parsedUser,
          access_token: storedToken, // Merge token manually
        });
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    } else {
      console.warn("User data or access token missing in localStorage.");
    }
  }, []);

  // Connect to the socket server when user is available
  useEffect(() => {
    const storedToken = localStorage.getItem("access_token");
  
    if (!storedToken) {
      console.warn("No access token found in localStorage.");
      return;
    }
  
    console.log("Attempting WebSocket connection with token:", storedToken);
  
    socketRef.current = io(SOCKET_SERVER_URL, {
      auth: { token: storedToken },
    });
  
    socketRef.current.on("connect", () => {
      console.log("✅ Connected to socket server");
      setIsConnected(true);
    });
  
    socketRef.current.on("disconnect", () => {
      console.log("❌ Disconnected from socket server");
      setIsConnected(false);
    });
  
    socketRef.current.on("error", (error) => {
      console.error("Socket error:", error);
      setIsConnected(false);
    });
  
    socketRef.current.on("connect_error", (error) => {
      console.error("Connection error:", error);  // Log detailed connection errors
      setIsConnected(false);
    });
  
    socketRef.current.on("receiveMessage", (message) => {
      console.log("📩 New message received:", message);  // Check if message structure is correct
      setMessages((prevMessages) => [...prevMessages, message]);  // Ensure message object has required fields
    });
    
    socketRef.current.on("previousMessages", (previousMessages) => {
      console.log("📩 Loading previous messages:", previousMessages);
      setMessages(previousMessages);
    });
  
    return () => {
      console.log("Closing WebSocket connection...");
      socketRef.current.disconnect();
    };
  }, []);
  

  // Auto scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = (e) => {
    e.preventDefault();
  
    if (newMessage.trim() === "" || !user || !user.id) {
      console.warn("Cannot send message, user data is invalid:", user);
      return;
    }
  
    let recipientId;
  
    // Determine if the sender is Admin or a regular User
    const isAdmin = user.name === "Admin";
    recipientId = isAdmin ? selectedUser?.id : "admin"; // Admin selects a user, users always message Admin
  
    if (!isAdmin && !recipientId) {
      console.warn("User must select a valid user to chat with.");
      return;
    }
  
    const messageToSend = {
      content: newMessage,
      senderId: user.id,
      receiverId: recipientId, // Use receiverId instead of toUsername
      senderType: isAdmin ? "admin" : "user",  // Mark sender as 'admin' or 'user'
      isRead: false,
    };
  
    console.log("Sending message:", messageToSend);
  
    if (socketRef.current) {
      socketRef.current.emit('sendMessage', messageToSend);
      setNewMessage("");  // Clear the input after sending
    }
  };
  
  
  

  const markMessagesAsRead = (senderId) => {
    if (socketRef.current && user) {
      socketRef.current.emit("markAsRead", { senderId });
    }
  };

  return (
    <div className="chat-page">
      <div className="chat-container">
        <div className="chat-header">
          <h2>User Chat</h2>
          <div className="connection-status">
            <span className={`status-indicator ${isConnected ? "online" : "offline"}`}></span>
            <span>{isConnected ? "Online" : "Offline"}</span>
          </div>
        </div>

        <div className="chat-content">
          {messages.map((msg, index) => (
            <div key={index} className={`message-bubble ${msg.senderType === "user" ? "user-message" : "admin-message"}`}>
              <div className="message-text">{msg.content}</div>
              <div className="message-time">{new Date(msg.createdAt).toLocaleTimeString()}</div> {/* Make sure `createdAt` exists */}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-area">
          <form onSubmit={handleSendMessage}>
            <input
              type="text"
              placeholder="Message"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              className="message-input"
            />
            <div className="input-actions">
              <button type="button" className="attachment-btn">
                <span className="material-icons">attach_file</span>
              </button>
              <button type="submit" className="send-btn">
                <span className="material-icons">send</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Chat;
